// HM-310 cluster 15: helper di mapping per la nuova dashboard SPEC v2.6.
// - status DB -> token palette + label visibile + icona Lucide + abbreviazione
// - countdown / ETA helper per tile e bottom strip

import {
  BedDouble,
  CalendarClock,
  CircleCheck,
  type LucideIcon,
  Sparkles,
  TriangleAlert,
} from '@hotel-manager/ui/icons';
import type { RoomDb } from '../../api/rooms.js';

export type RoomStatusToken =
  | 'state-lib'
  | 'state-ris'
  | 'state-occ'
  | 'state-sprc'
  | 'state-guas'
  | 'state-puliz';

export interface RoomStatusInfo {
  token: RoomStatusToken;
  label: string;
  abbr: string;
  icon: LucideIcon;
  /** Tailwind className per text/border standard (token diretti). */
  classText: string;
  classBorder: string;
  classBg: string;
  /** HM-312.r: tinted background per RoomTile (mockup §room-tile bg). */
  tileBgClass: string;
  /** HM-312.r: status label color piu' scuro per leggibilita su tile bg. */
  tileStatusClass: string;
  /** HM-312.r: hex border per inline style 1.5px (Tailwind non ha 1.5 nativo). */
  borderHex: string;
}

// I valori bg / border / text scuro derivano da
// docs/design-references/dashboard-v2-reference.html (.state-* CSS rules).
const FREE: RoomStatusInfo = {
  token: 'state-lib',
  label: 'Libera',
  abbr: 'LIB',
  icon: CircleCheck,
  classText: 'text-state-lib',
  classBorder: 'border-state-lib',
  classBg: 'bg-state-lib',
  tileBgClass: 'bg-[#F4FAF1]',
  tileStatusClass: 'text-[#5F8A50]',
  borderHex: '#8BAE7C',
};
const RESERVED: RoomStatusInfo = {
  token: 'state-ris',
  label: 'Riservata',
  abbr: 'RIS',
  icon: CalendarClock,
  classText: 'text-state-ris',
  classBorder: 'border-state-ris',
  classBg: 'bg-state-ris',
  tileBgClass: 'bg-[#F0F4F8]',
  tileStatusClass: 'text-[#4A6985]',
  borderHex: '#6B8AAB',
};
const OCCUPIED: RoomStatusInfo = {
  token: 'state-occ',
  label: 'Occupata',
  abbr: 'OCC',
  icon: BedDouble,
  classText: 'text-state-occ',
  classBorder: 'border-state-occ',
  classBg: 'bg-state-occ',
  tileBgClass: 'bg-[#FCF1EA]',
  tileStatusClass: 'text-[#C4661F]',
  borderHex: '#E97B3D',
};
const DIRTY: RoomStatusInfo = {
  token: 'state-sprc',
  label: 'Da pulire',
  abbr: 'SPRC',
  icon: Sparkles,
  classText: 'text-state-sprc',
  classBorder: 'border-state-sprc',
  classBg: 'bg-state-sprc',
  tileBgClass: 'bg-[#FAF1ED]',
  tileStatusClass: 'text-[#8B5235]',
  borderHex: '#C97B5A',
};
const OUT_OF_ORDER: RoomStatusInfo = {
  token: 'state-guas',
  label: 'Guasta',
  abbr: 'GUAS',
  icon: TriangleAlert,
  classText: 'text-state-guas',
  classBorder: 'border-state-guas',
  classBg: 'bg-state-guas',
  tileBgClass: 'bg-[#FBEEEC]',
  tileStatusClass: 'text-[#7D2E26]',
  borderHex: '#A8453A',
};

export function mapRoomStatus(status: RoomDb['status']): RoomStatusInfo {
  switch (status) {
    case 'free':
      return FREE;
    case 'reserved':
      return RESERVED;
    case 'occupied':
      return OCCUPIED;
    case 'dirty':
      return DIRTY;
    case 'out_of_order':
      return OUT_OF_ORDER;
  }
}

/** "tra 2h 15m" / "tra 45m" / "ora" / "scaduto 12m fa" */
export function formatRelative(target: Date, now: Date): string {
  const diff = target.getTime() - now.getTime();
  const abs = Math.abs(diff);
  const min = Math.floor(abs / 60_000);
  const h = Math.floor(min / 60);
  const remMin = min % 60;
  const part = h > 0 ? `${h}h ${remMin}m` : `${remMin}m`;
  if (Math.abs(diff) < 60_000) return 'ora';
  return diff > 0 ? `tra ${part}` : `${part} fa`;
}

/** "14:30" formato HH:MM zero-padded */
export function formatTime(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** "Mario R." -> due iniziali per avatar; mantiene casing */
export function initialsOf(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts
    .map((p) => p.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2);
}
